import { Injector } from '@angular/core';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { GetTokenSilentlyOptions, LogoutOptions, User } from '@auth0/auth0-spa-js';
import { Observable, of } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { FrontendLoggerService } from '../frontend-logger.service';
import { TokenService } from '../token.service';
import { AuthFlowStateService } from './auth-flow-state.service';

export class AuthSessionClient {
  constructor(
    private readonly auth0: Auth0Service,
    private readonly logger: FrontendLoggerService,
    private readonly tokenService: TokenService,
    private readonly flowState: AuthFlowStateService,
    private readonly injector: Injector
  ) {}

  logConfiguration(): void {
    this.logger.info('Inicializando Auth0 client');
    const audience = environment.auth0.authorizationParams.audience;
    this.logger.debug('Auth0 audience (runtime)', audience || 'NONE');
    if (!environment.production) return;
    if (!audience) {
      this.logger.warn(
        'Audience vacío en producción: el access token no se emitirá (solo ID token).'
      );
    } else if (/localhost/i.test(audience)) {
      this.logger.error(
        'Audience apunta a localhost en producción. Ajusta environment.prod.ts (PRODUCTION_AUDIENCE).',
        audience
      );
    }
  }

  login(target: string, onFailure: () => void): Observable<void> {
    this.logger.info('Iniciando login', target);
    return this.auth0.loginWithRedirect({
      appState: { target },
      authorizationParams: {
        audience: environment.auth0.authorizationParams.audience,
        scope: environment.auth0.authorizationParams.scope,
      },
    }).pipe(
      tap(() => this.logger.debug('loginWithRedirect lanzado', target)),
      catchError((error) => {
        this.logger.error(
          'Error en loginWithRedirect',
          error?.message || JSON.stringify(error)
        );
        onFailure();
        return of();
      })
    );
  }

  signup(target: string): Observable<void> {
    this.logger.info('Iniciando signup', target);
    return this.auth0.loginWithRedirect({
      appState: { target },
      authorizationParams: {
        screen_hint: 'signup',
        audience: environment.auth0.authorizationParams.audience,
        scope: environment.auth0.authorizationParams.scope,
        ...(environment.production ? { prompt: 'consent' } : {}),
      },
    }).pipe(
      tap(() => this.logger.debug('loginWithRedirect signup lanzado', target)),
      catchError((error) => {
        this.logger.error(
          'Error en signupWithRedirect',
          error?.message || JSON.stringify(error)
        );
        return of();
      })
    );
  }

  handleRedirectCallback(): Observable<any> {
    this.logger.info('Ejecutando handleRedirectCallback');
    return this.auth0.handleRedirectCallback().pipe(
      tap((result) => {
        this.logger.debug('Resultado handleRedirectCallback', JSON.stringify(result));
      }),
      catchError((error) => {
        this.logger.error(
          'Error en handleRedirectCallback pipe',
          error?.message || JSON.stringify(error)
        );
        return of({});
      })
    );
  }

  logout(options?: LogoutOptions): void {
    this.logger.info('Iniciando logout');
    this.disconnectNotifications();
    try {
      this.auth0.logout({
        ...options,
        logoutParams: {
          returnTo: globalThis.location?.origin ?? '',
          ...(options?.logoutParams ?? {}),
        },
      });
      this.flowState.clearResolvedFlow();
      this.flowState.clearPendingInviteContext();
      this.logger.info('Logout exitoso');
    } catch (error) {
      this.logger.error(
        'Error durante logout',
        (error as Error)?.message || JSON.stringify(error)
      );
    }
  }

  isAuthenticated(): Observable<boolean> {
    return this.auth0.isAuthenticated$.pipe(take(1));
  }

  getUserProfile(): Observable<User | null> {
    return this.auth0.user$.pipe(
      take(1),
      map((user) => {
        this.logger.debug('Perfil usuario cargado', user ? user.sub || 'sin-sub' : 'null');
        return user ?? null;
      }),
      catchError((error) => {
        this.logger.error('Error getUserProfile', error?.message || JSON.stringify(error));
        return of(null);
      })
    );
  }

  getAccessToken(options?: GetTokenSilentlyOptions): Observable<string> {
    return this.tokenService.getToken(options).pipe(
      tap((token) => {
        if (token) {
          this.logger.debug('Token obtenido silencioso', 'ok');
        } else if ((globalThis.location?.pathname ?? '') !== '/login') {
          this.logger.warn('No se pudo obtener token desde la fuente unificada de Auth0.');
        }
      })
    );
  }

  rawRedirectCallback(): Observable<any> {
    return this.auth0.handleRedirectCallback();
  }

  private disconnectNotifications(): void {
    try {
      const notifier = this.injector.get<any>('NotificationService' as any);
      if (notifier && typeof notifier.disconnect === 'function') {
        try {
          notifier.disconnect();
        } catch (error) {
          this.logger.debug(
            'NotificationService disconnect failed during logout',
            (error as Error)?.message || String(error)
          );
        }
      }
    } catch (error) {
      this.logger.debug(
        'NotificationService unavailable during logout',
        (error as Error)?.message || String(error)
      );
    }
  }
}
